import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { confirmLeaveUnsaved } from '../unsavedGuard';

function PersonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.5 15a4.5 4.5 0 0 1 9 0H3.5Z" />
    </svg>
  );
}

const DROPDOWN_STYLE: React.CSSProperties = {
  position: 'absolute',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(16,24,40,0.12)',
  zIndex: 300,
  overflow: 'hidden',
};

/**
 * Global site header. Public: shows the brand + a Sign In link when signed out,
 * or the user's display name + Sign out when signed in. Never gates navigation.
 */
export default function SiteHeader({ compact = false }: { compact?: boolean }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (navRef.current && !navRef.current.contains(e.target as Node)) setNavOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  return (
    <nav className={`app-nav${compact ? ' app-nav--compact' : ''}`} style={{ flexWrap: 'nowrap' }}>
      <div className="app-nav-left" ref={navRef}>
        <Link to="/" className="app-nav-brand">Speed Duel</Link>
        <button
          type="button"
          className="nav-hamburger"
          aria-label="Menu"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(o => !o)}
        >
          <span /><span /><span />
        </button>
        <div className={`app-nav-links${navOpen ? ' open' : ''}`}>
          <Link to="/" className="app-nav-link" onClick={() => setNavOpen(false)}>Cards</Link>
          <Link to="/decks" className="app-nav-link" onClick={() => setNavOpen(false)}>Decks</Link>
          <Link to="/duel" className="app-nav-link" onClick={() => setNavOpen(false)}>Duel</Link>
          <Link to="/rulings" className="app-nav-link" onClick={() => setNavOpen(false)}>Rulings</Link>
        </div>
      </div>

      <div ref={userRef} style={{ position: 'relative', flexShrink: 0 }}>
        {user ? (
          <>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: '5px 10px',
                fontSize: 13,
                cursor: 'pointer',
                color: 'var(--color-text)',
                whiteSpace: 'nowrap',
              }}
            >
              <PersonIcon />
              <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.displayName}
              </span>
              <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>
                {menuOpen ? '▲' : '▼'}
              </span>
            </button>
            {menuOpen && (
              <div style={{ ...DROPDOWN_STYLE, top: 'calc(100% + 6px)', right: 0, minWidth: 150 }}>
                <Link
                  to={`/user/${user.userId}`}
                  onClick={() => setMenuOpen(false)}
                  style={{ display: 'block', padding: '10px 16px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' }}
                >
                  My profile
                </Link>
                <button
                  type="button"
                  onClick={() => { if (!confirmLeaveUnsaved()) return; logout(); setMenuOpen(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 16px',
                    fontSize: 13,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'var(--color-text)',
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </>
        ) : (
          <Link
            to="/login"
            state={{ from: location.pathname + location.search }}
            className="app-nav-link"
            style={{ border: '1px solid var(--color-border)' }}
          >
            Sign In
          </Link>
        )}
      </div>
    </nav>
  );
}
